import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { workflowChangeSetsApi, type ChangeSetItemDecision } from '@/api/workflowChangeSets'
import { useTenantKey } from './useTenantKey'

export function useWorkflowRunChangeSets(projectId: string, runId: string | null, enabled = true) {
  const tenantKey = useTenantKey()
  return useQuery({
    queryKey: tenantKey('projects', projectId, 'workflow-runs', runId, 'change-sets'),
    queryFn: () => workflowChangeSetsApi.getChangeSets(projectId, runId!),
    enabled: !!projectId && !!runId && enabled,
    staleTime: 2_000,
  })
}

export function useSetChangeSetItemDecision(projectId: string, runId: string) {
  const queryClient = useQueryClient()
  const tenantKey = useTenantKey()
  return useMutation({
    mutationFn: ({ changeSetId, itemId, decision }: { changeSetId: string; itemId: string; decision: ChangeSetItemDecision }) =>
      workflowChangeSetsApi.setItemDecision(projectId, runId, changeSetId, itemId, decision),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: tenantKey('projects', projectId, 'workflow-runs', runId, 'change-sets') })
    },
  })
}

export function useEditChangeSetItemProposedContent(projectId: string, runId: string) {
  const queryClient = useQueryClient()
  const tenantKey = useTenantKey()
  return useMutation({
    mutationFn: ({ changeSetId, itemId, proposedValuesJson }: { changeSetId: string; itemId: string; proposedValuesJson: string }) =>
      workflowChangeSetsApi.editItemProposedContent(projectId, runId, changeSetId, itemId, proposedValuesJson),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: tenantKey('projects', projectId, 'workflow-runs', runId, 'change-sets') })
      toast.success('Changes saved.')
    },
  })
}

export function useSubmitGateFeedback(projectId: string, runId: string | null) {
  const queryClient = useQueryClient()
  const tenantKey = useTenantKey()
  return useMutation({
    mutationFn: (message: string) => workflowChangeSetsApi.submitGateFeedback(projectId, runId!, message),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: tenantKey('projects', projectId, 'workflow-runs') })
    },
  })
}
